/**
 * App-side wiring for the `@heidi/answer-kit` door.
 *
 * The kit renders the markdown vocabulary; it does not know what an in-app
 * href is. This module supplies that one answer: a settings route resolves to
 * a handler that opens the settings modal in place, and everything else
 * resolves to `undefined` so the renderer falls through to its external-link
 * path.
 *
 * The settings vocabulary (`ROUTE_TO_SLUG`) and the `?settings=` protocol stay
 * here on purpose — `src/features/settings` is a product domain, and the kit
 * reaching it would be an import a package may not make.
 *
 * Imported for effect at module scope from `_app.tsx`, beside
 * `plugin-core-wiring`, so it evaluates before any answer surface mounts. The
 * seam defaults to `() => undefined`, so forgetting this call costs in-place
 * settings navigation and nothing else — an invisible failure, which is why
 * the two settings-anchor cases in
 * `src/components/chat/components/markdown-components.test.tsx` import this
 * module and go red without the call below.
 */
import {
  ROUTE_TO_SLUG,
  SETTINGS_QUERY_PARAM,
} from '@/features/settings/atoms/settingsModalSlugs';
import { configureAnswerKit } from '@heidi/answer-kit';

/**
 * Check if an href is an internal settings route that should open the
 * settings modal in-place rather than navigating to a new tab.
 */
const getSettingsSlug = (href: string | undefined): string | undefined => {
  if (!href) return undefined;
  return ROUTE_TO_SLUG[href];
};

/**
 * Open the settings modal by updating the current URL's query param.
 * The `useSettingsModalUrlSync` hook picks up the change and opens the modal.
 */
const openSettingsModal = (slug: string): void => {
  const params = new URLSearchParams(window.location.search);
  params.set(SETTINGS_QUERY_PARAM, slug);
  const search = params.toString();
  const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;
  window.history.pushState(window.history.state, '', newUrl);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

configureAnswerKit({
  resolveInternalHref: (href) => {
    const slug = getSettingsSlug(href);
    if (!slug) return undefined;
    return () => openSettingsModal(slug);
  },
});
